import { api } from "@clarent/backend/convex/_generated/api";
import { Link, useLocation } from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import type { Doc } from "@clarent/backend/convex/_generated/dataModel";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { errorMessage } from "../lib/convexError";
import { formatHours, plural } from "../lib/format";
import { useMachines } from "../lib/machines";

const WEEKDAYS = [
  { value: 1, label: "Mon" },
  { value: 2, label: "Tue" },
  { value: 3, label: "Wed" },
  { value: 4, label: "Thu" },
  { value: 5, label: "Fri" },
  { value: 6, label: "Sat" },
  { value: 0, label: "Sun" },
];

export function SettingsPage() {
  const settings = useQuery(api.settings.get);
  const update = useMutation(api.settings.update);

  const [workingDays, setWorkingDays] = useState<number[]>([]);
  const [alertLeadDays, setLead] = useState("");
  const [message, setMessage] = useState<{ kind: "ok" | "error"; text: string } | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!settings) return;
    setWorkingDays(settings.workingDays);
    setLead(String(settings.alertLeadDays));
  }, [settings]);

  const toggleDay = (d: number) => setWorkingDays((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      await update({ workingDays, alertLeadDays: Number(alertLeadDays) });
      setMessage({ kind: "ok", text: "Saved. Every job's latest start has been recomputed." });
    } catch (err) {
      setMessage({ kind: "error", text: errorMessage(err) });
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return <p className="text-sm text-muted">Loading settings…</p>;

  return (
    <div className="mx-auto max-w-2xl">
      <h1 className="page-title mb-5">Shop week</h1>
      <form onSubmit={submit} className="panel space-y-6 p-6">
        <fieldset>
          <legend className="label">Working days</legend>
          <div className="flex flex-wrap gap-2">
            {WEEKDAYS.map((d) => {
              const on = workingDays.includes(d.value);
              return (
                <button
                  key={d.value}
                  type="button"
                  aria-pressed={on}
                  onClick={() => toggleDay(d.value)}
                  className={`btn ${on ? "bg-brand text-white hover:bg-brand-dark" : "border-line bg-bg text-ink hover:bg-surface"}`}
                >
                  {d.label}
                </button>
              );
            })}
          </div>
        </fieldset>

        <div className="grid gap-4 sm:grid-cols-3">
          <div>
            <label className="label" htmlFor="lead">
              Alert lead (working days)
            </label>
            <input id="lead" type="number" min={0} step={1} className="input input-data" value={alertLeadDays} onChange={(e) => setLead(e.target.value)} required />
            <p className="hint">Warn when a job must start within this many days.</p>
          </div>
        </div>

        {message && (
          <p className={message.kind === "ok" ? "notice-ok" : "notice-error"}>
            {message.text}
          </p>
        )}

        <div>
          <button type="submit" className="btn-primary btn-md" disabled={saving}>
            {saving ? "Saving…" : "Save settings"}
          </button>
        </div>
      </form>
      <Machines workingDays={settings.workingDays} />
      <ArchivedJobs />
    </div>
  );
}

/**
 * Decision D49: each machine has its own weekly hours, and a job's machining is split across
 * machines in the job form. Machines with hours on live jobs can't be archived.
 */
function Machines({ workingDays }: { workingDays: readonly number[] }) {
  const machines = useMachines();
  const create = useMutation(api.machines.create);
  const restore = useMutation(api.machines.restore);
  const [name, setName] = useState("");
  const [hours, setHours] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  // A new machine starts with the hours of the last one added, so a shop of identical machines types each number once.
  const lastHours = machines?.active.at(-1)?.weeklyHours;
  const hoursValue = hours === "" && lastHours !== undefined ? String(lastHours) : hours;

  const add = async (e: FormEvent) => {
    e.preventDefault();
    setAdding(true);
    setError(null);
    try {
      await create({ name, weeklyHours: Number(hoursValue) });
      setName("");
      setHours("");
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setAdding(false);
    }
  };

  const archived = machines?.all.filter((m) => m.archivedAt !== undefined) ?? [];
  const perDay = (weekly: number) => (workingDays.length > 0 ? weekly / workingDays.length : 0);

  return (
    <section id="machines" className="panel mt-6 p-6">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="app-title">Machines</h2>
        {machines && (
          <span className="font-mono text-xs text-muted">
            {plural(machines.active.length, "machine")} · {formatHours(machines.weeklyHours)} a week
          </span>
        )}
      </div>
      <p className="mt-1 text-sm text-muted">Each machine runs its own hours. In a job, split the machining across the machines it will run on.</p>
      {error && <p className="notice-error mt-3">{error}</p>}
      {machines === undefined && <p className="mt-3 text-sm text-muted">Loading…</p>}
      {machines && (
        <ul className="mt-3 divide-y divide-line">
          {machines.active.map((m) => (
            <MachineRow key={m._id} machine={m} perDay={perDay(m.weeklyHours)} onError={setError} />
          ))}
        </ul>
      )}
      <form onSubmit={add} className="mt-3 flex flex-wrap items-end gap-3 border-t border-line pt-4">
        <div className="min-w-0 flex-1">
          <label className="label" htmlFor="new-machine-name">
            Name or number
          </label>
          <input id="new-machine-name" className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. M4 or Mazak 2" maxLength={30} required />
        </div>
        <div className="w-36">
          <label className="label" htmlFor="new-machine-hours">
            Hours a week
          </label>
          <input id="new-machine-hours" type="number" min={0} max={168} step={0.5} className="input input-data" value={hoursValue} onChange={(e) => setHours(e.target.value)} required />
        </div>
        <button type="submit" className="btn-primary btn-md" disabled={adding}>
          {adding ? "Adding…" : "Add machine"}
        </button>
      </form>
      {archived.length > 0 && (
        <div className="mt-5">
          <h3 className="text-[13px] font-extrabold">Archived</h3>
          <ul className="mt-1 divide-y divide-line">
            {archived.map((m) => (
              <li key={m._id} className="flex items-center gap-3 py-2">
                <span className="text-sm text-muted">{m.name}</span>
                <span className="font-mono text-[11px] text-subtle">{formatHours(m.weeklyHours)} a week</span>
                <button
                  type="button"
                  className="btn-ghost ml-auto"
                  onClick={async () => {
                    try {
                      setError(null);
                      await restore({ machineId: m._id });
                    } catch (e) {
                      setError(errorMessage(e));
                    }
                  }}
                >
                  Restore
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

/** One machine: name and weekly hours edited in place, saved with the row's button. */
function MachineRow({ machine, perDay, onError }: { machine: Doc<"machines">; perDay: number; onError: (message: string | null) => void }) {
  const update = useMutation(api.machines.update);
  const archive = useMutation(api.machines.archive);
  const [name, setName] = useState(machine.name);
  const [hours, setHours] = useState(String(machine.weeklyHours));
  const [saving, setSaving] = useState(false);
  const changed = name !== machine.name || Number(hours) !== machine.weeklyHours;

  useEffect(() => {
    setName(machine.name);
    setHours(String(machine.weeklyHours));
  }, [machine.name, machine.weeklyHours]);

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    onError(null);
    try {
      await update({ machineId: machine._id, name, weeklyHours: Number(hours) });
    } catch (err) {
      onError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <li>
      <form onSubmit={save} className="flex flex-wrap items-center gap-3 py-2.5">
        <input className="input h-9 min-w-0 flex-1 rounded-[10px] px-2.5 text-sm font-bold" value={name} onChange={(e) => setName(e.target.value)} aria-label={`Name of ${machine.name}`} maxLength={30} required />
        <label className="flex h-9 w-36 shrink-0 items-center gap-1.5 rounded-[10px] border border-line bg-bg px-2.5 focus-within:border-brand focus-within:ring-4 focus-within:ring-brand-tint">
          <input
            type="number"
            className="w-full min-w-0 bg-transparent font-mono text-sm outline-none"
            min={0}
            max={Math.max(168, machine.weeklyHours)}
            step={0.5}
            value={hours}
            onChange={(e) => setHours(e.target.value)}
            aria-label={`${machine.name} hours a week`}
            required
          />
          <span className="text-xs whitespace-nowrap text-muted">h a week</span>
        </label>
        <span className="w-24 font-mono text-[11px] text-subtle">≈ {perDay.toFixed(1)} h a day</span>
        {changed ? (
          <button type="submit" className="btn-primary h-9 px-3.5" disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </button>
        ) : (
          <button
            type="button"
            className="btn-ghost"
            onClick={async () => {
              try {
                onError(null);
                await archive({ machineId: machine._id });
              } catch (err) {
                onError(errorMessage(err));
              }
            }}
          >
            Archive
          </button>
        )}
      </form>
    </li>
  );
}

const archivedOn = new Intl.DateTimeFormat("en-GB", { weekday: "short", day: "numeric", month: "short", timeZone: "Europe/London" });

/** Decision D29: archived jobs can be restored from here. */
function ArchivedJobs() {
  const jobs = useQuery(api.jobs.listArchived);
  const restore = useMutation(api.jobs.restore);
  const [error, setError] = useState<string | null>(null);
  // The account page links to #archived; this section only exists once settings have loaded.
  const hash = useLocation({ select: (l) => l.hash });
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    if (hash === "archived") ref.current?.scrollIntoView({ block: "start" });
  }, [hash]);

  return (
    <section ref={ref} id="archived" className="panel mt-6 scroll-mt-20 p-6 lg:scroll-mt-6">
      <h2 className="app-title">Archived jobs</h2>
      <p className="mt-1 text-sm text-muted">Archived jobs are off the board and calendar and don't raise alerts.</p>
      {error && <p className="notice-error mt-3">{error}</p>}
      {jobs === undefined && <p className="mt-3 text-sm text-muted">Loading…</p>}
      {jobs && jobs.length === 0 && <p className="mt-3 text-sm text-subtle">Nothing archived.</p>}
      {jobs && jobs.length > 0 && (
        <ul className="mt-3 divide-y divide-line">
          {jobs.map((job) => (
            <li key={job._id} className="flex items-center gap-3 py-2.5">
              <Link to="/jobs/$jobId" params={{ jobId: job._id }} className="font-mono text-xs text-muted hover:text-brand-dark hover:underline">
                {job.reference}
              </Link>
              <span className="text-sm font-bold">{job.customerName}</span>
              <span className="ml-auto font-mono text-[11px] text-muted">Archived {archivedOn.format(job.archivedAt)}</span>
              <button
                type="button"
                className="btn-ghost"
                onClick={async () => {
                  try {
                    setError(null);
                    await restore({ jobId: job._id });
                  } catch (e) {
                    setError(errorMessage(e));
                  }
                }}
              >
                Restore
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
